import { apiSlice } from "./apiSlice";

const NOTIFICATIONS_URL = "/notifications";

// Data payload types for each notification type
export interface CircleInviteData {
  circleId: string;
  circleName: string;
}

export interface MemberAcceptedData {
  circleId: string;
  circleName: string;
  memberName: string;
}

export interface CircleStartedData {
  circleId: string;
  circleName: string;
}

export interface PaymentUpcomingData {
  circleId: string;
  circleName: string;
  amount: number;
  dueDate: string;
  currency: string;
}

export interface PaymentOverdueData {
  circleId: string;
  circleName: string;
  amount: number;
  currency: string;
}

export interface WelcomeData {
  action: "setup_account";
}

export interface AccountVerifiedData {
  action: "browse_circles";
}

export type NotificationData =
  | CircleInviteData
  | MemberAcceptedData
  | CircleStartedData
  | PaymentUpcomingData
  | PaymentOverdueData
  | WelcomeData
  | AccountVerifiedData;

export interface Notification {
  id: number;
  UserId: number;
  type: string;
  title: string;
  body: string;
  data: NotificationData;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  nextCursor: string | null;
}

export interface UnreadCountResponse {
  count: number;
}

export const notificationsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<
      NotificationsResponse,
      { cursor?: string; limit?: number }
    >({
      query: ({ cursor, limit = 10 }) => ({
        url: NOTIFICATIONS_URL,
        method: "GET",
        params: { cursor, limit },
      }),
      // Keep all paginated results under the same cache key
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      // Merge new notifications with existing ones
      merge: (currentCache, newItems, { arg }) => {
        if (!arg?.cursor) {
          // Fresh fetch (no cursor) — replace the first page and keep any extra paginated items
          const newIds = new Set(newItems.notifications.map((n) => n.id));
          // Keep paginated items that aren't in the fresh page
          const paginatedOnly = currentCache.notifications.filter(
            (n) => !newIds.has(n.id)
          );
          currentCache.notifications = [
            ...newItems.notifications,
            ...paginatedOnly,
          ];
        } else if (newItems.notifications.length > 0) {
          // Paginating — append only genuinely new items
          const existingIds = new Set(
            currentCache.notifications.map((n) => n.id)
          );
          const uniqueNewItems = newItems.notifications.filter(
            (n) => !existingIds.has(n.id)
          );
          currentCache.notifications.push(...uniqueNewItems);
        }
        currentCache.nextCursor = newItems.nextCursor;
      },
      // Always refetch when cursor changes
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.cursor !== previousArg?.cursor;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.notifications.map(({ id }) => ({
                type: "Notification" as const,
                id,
              })),
              { type: "Notification", id: "LIST" },
            ]
          : [{ type: "Notification", id: "LIST" }],
    }),
    getUnreadCount: builder.query<UnreadCountResponse, void>({
      query: () => ({
        url: `${NOTIFICATIONS_URL}/unread-count`,
        method: "GET",
      }),
      providesTags: [{ type: "Notification", id: "UNREAD_COUNT" }],
    }),
    readNotification: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `${NOTIFICATIONS_URL}/${id}/read`,
        method: "PATCH",
      }),
      // Optimistic update — immediately mark notification as read in cache
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchNotifications = dispatch(
          notificationsApiSlice.util.updateQueryData(
            "getNotifications",
            // arg doesn't matter — serializeQueryArgs collapses all to one key
            { limit: 10 },
            (draft) => {
              const notification = draft.notifications.find((n) => n.id === id);
              if (notification) {
                notification.isRead = true;
                notification.readAt = new Date().toISOString();
              }
            }
          )
        );
        const patchUnread = dispatch(
          notificationsApiSlice.util.updateQueryData(
            "getUnreadCount",
            undefined,
            (draft) => {
              if (draft.count > 0) draft.count -= 1;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchNotifications.undo();
          patchUnread.undo();
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Notification", id },
        { type: "Notification", id: "UNREAD_COUNT" },
      ],
    }),
    readAllNotifications: builder.mutation<void, void>({
      query: () => ({
        url: `${NOTIFICATIONS_URL}/read-all`,
        method: "PATCH",
      }),
      // Optimistic update — mark all notifications as read in cache
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchNotifications = dispatch(
          notificationsApiSlice.util.updateQueryData(
            "getNotifications",
            { limit: 10 },
            (draft) => {
              for (const notification of draft.notifications) {
                if (!notification.isRead) {
                  notification.isRead = true;
                  notification.readAt = new Date().toISOString();
                }
              }
            }
          )
        );
        const patchUnread = dispatch(
          notificationsApiSlice.util.updateQueryData(
            "getUnreadCount",
            undefined,
            (draft) => {
              draft.count = 0;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchNotifications.undo();
          patchUnread.undo();
        }
      },
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "Notification", id: "UNREAD_COUNT" },
      ],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useReadNotificationMutation,
  useReadAllNotificationsMutation,
} = notificationsApiSlice;
